import { useCallback, useState } from 'react'
import { useMutation } from '@apollo/client'
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import EditIcon from '@mui/icons-material/Edit'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { Alert, Button } from 'ui/baseComponents'
import Loading from 'ui/components/Loading'
import AdminPageHeader from 'ui/screens/Admin/AdminPageHeader'
import { formatDateString } from 'utilities/time'
import { useComplianceReport } from './graphql/complianceReportQueries'
import {
  useComplianceConfigurationList,
  LIST_COMPLIANCE_CONFIGURATIONS,
} from './graphql/complianceConfigurationQueries'
import {
  CREATE_COMPLIANCE_CONFIGURATION,
  UPDATE_COMPLIANCE_CONFIGURATION,
  DELETE_COMPLIANCE_CONFIGURATION,
} from './graphql/complianceConfigurationMutations'
import { useOrgsWithProjects } from './graphql/projectLookupQueries'
import {
  useComplianceExemptionList,
  LIST_COMPLIANCE_EXEMPTIONS,
} from './graphql/complianceExemptionQueries'
import {
  CREATE_COMPLIANCE_EXEMPTION,
  UPDATE_COMPLIANCE_EXEMPTION,
  DELETE_COMPLIANCE_EXEMPTION,
} from './graphql/complianceExemptionMutations'
import ConfigurationDialog from './tabs/ComplianceConfigurationsTab/ConfigurationDialog'
import ExemptionDialog from './tabs/ComplianceExemptionsTab/ExemptionDialog'
import CsvUploadDialog from './tabs/ComplianceExemptionsTab/CsvUploadDialog'

const CATEGORY_LABELS = {
  mobility: 'Mobility',
  tremor_dyskinesia: 'Tremor & Dyskinesia',
  sleep: 'Sleep',
}

const REASON_LABELS = {
  medical: 'Medical',
  personal: 'Personal',
  non_responsive: 'Non-Responsive',
}

const formatCategory = (cat) => CATEGORY_LABELS[cat] || cat
const formatReason = (reason) => REASON_LABELS[reason] || reason

const SectionHeader = ({ title, action }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      mb: 2,
    }}
  >
    <Typography variant="h6">{title}</Typography>
    {action}
  </Box>
)

const CompliancePage = () => {
  const [fetchReport, { loading: reportLoading }] = useComplianceReport()
  const [reportError, setReportError] = useState(null)

  const {
    loading: configsLoading,
    error: configsError,
    data: configsData,
  } = useComplianceConfigurationList()
  const { orgs } = useOrgsWithProjects()
  const [configSearch, setConfigSearch] = useState('')
  const [configDialogOpen, setConfigDialogOpen] = useState(false)
  const [editingConfig, setEditingConfig] = useState(null)
  const [deleteConfigId, setDeleteConfigId] = useState(null)

  const {
    loading: exemptionsLoading,
    error: exemptionsError,
    data: exemptionsData,
  } = useComplianceExemptionList()
  const [exemptionSearch, setExemptionSearch] = useState('')
  const [exemptionDialogOpen, setExemptionDialogOpen] = useState(false)
  const [editingExemption, setEditingExemption] = useState(null)
  const [deleteExemptionId, setDeleteExemptionId] = useState(null)
  const [csvDialogOpen, setCsvDialogOpen] = useState(false)

  const configRefetch = {
    refetchQueries: [{ query: LIST_COMPLIANCE_CONFIGURATIONS }],
  }
  const exemptionRefetch = {
    refetchQueries: [{ query: LIST_COMPLIANCE_EXEMPTIONS }],
  }

  const [createConfig, { loading: creatingConfig }] = useMutation(
    CREATE_COMPLIANCE_CONFIGURATION,
    configRefetch,
  )
  const [updateConfig, { loading: updatingConfig }] = useMutation(
    UPDATE_COMPLIANCE_CONFIGURATION,
    configRefetch,
  )
  const [deleteConfig] = useMutation(
    DELETE_COMPLIANCE_CONFIGURATION,
    configRefetch,
  )

  const [createExemption, { loading: creatingExemption }] = useMutation(
    CREATE_COMPLIANCE_EXEMPTION,
    exemptionRefetch,
  )
  const [updateExemption, { loading: updatingExemption }] = useMutation(
    UPDATE_COMPLIANCE_EXEMPTION,
    exemptionRefetch,
  )
  const [deleteExemption] = useMutation(
    DELETE_COMPLIANCE_EXEMPTION,
    exemptionRefetch,
  )

  const projectMap = useMemo(() => {
    const map = {}
    for (const org of orgs) {
      for (const project of org.projectList?.projects || []) {
        map[project.id] = { title: project.title, orgName: org.displayName }
      }
    }
    return map
  }, [orgs])

  const configs = useMemo(
    () => configsData?.projectComplianceConfigList?.configs || [],
    [configsData],
  )

  const filteredConfigs = useMemo(() => {
    if (!configSearch.trim()) return configs
    const q = configSearch.toLowerCase()
    return configs.filter((c) => {
      const p = projectMap[c.projectId]
      return (
        c.projectId.toLowerCase().includes(q) ||
        p?.title.toLowerCase().includes(q) ||
        p?.orgName.toLowerCase().includes(q)
      )
    })
  }, [configs, configSearch, projectMap])

  const exemptions = useMemo(
    () => exemptionsData?.complianceExemptionList?.exemptions || [],
    [exemptionsData],
  )

  const filteredExemptions = useMemo(() => {
    if (!exemptionSearch.trim()) return exemptions
    const q = exemptionSearch.toLowerCase()
    return exemptions.filter(
      (ex) =>
        ex.patientId.toLowerCase().includes(q) ||
        (ex.reason && ex.reason.toLowerCase().includes(q)) ||
        (ex.detail && ex.detail.toLowerCase().includes(q)),
    )
  }, [exemptions, exemptionSearch])

  const handleDownload = useCallback(async () => {
    setReportError(null)
    try {
      const { data, error } = await fetchReport()
      if (error) {
        setReportError(error.message)
        return
      }
      const url = data?.complianceReport?.url
      if (url) window.open(url, '_blank')
      else setReportError('No compliance report available yet.')
    } catch (err) {
      setReportError(err.message)
    }
  }, [fetchReport])

  const handleConfigSubmit = useCallback(
    async (form) => {
      const input = {
        projectId: form.projectId,
        categories: form.categories,
        complianceThresholdDays: form.complianceThresholdDays,
        outreachGapDays: form.outreachGapDays,
        maxOutreachAttempts: form.maxOutreachAttempts,
      }
      if (editingConfig) await updateConfig({ variables: { input } })
      else await createConfig({ variables: { input } })
      setConfigDialogOpen(false)
      setEditingConfig(null)
    },
    [editingConfig, createConfig, updateConfig],
  )

  const handleDeleteConfig = useCallback(
    async (projectId) => {
      await deleteConfig({ variables: { projectId } })
      setDeleteConfigId(null)
    },
    [deleteConfig],
  )

  const handleExemptionSubmit = useCallback(
    async (form) => {
      const input = {
        patientId: form.patientId,
        reason: form.reason,
        categories: form.categories,
        detail: form.detail || null,
      }
      if (editingExemption) await updateExemption({ variables: { input } })
      else await createExemption({ variables: { input } })
      setExemptionDialogOpen(false)
      setEditingExemption(null)
    },
    [editingExemption, createExemption, updateExemption],
  )

  const handleDeleteExemption = useCallback(
    async (patientId) => {
      await deleteExemption({ variables: { patientId } })
      setDeleteExemptionId(null)
    },
    [deleteExemption],
  )

  const handleCsvUploadRow = useCallback(
    async (row) => {
      await createExemption({
        variables: {
          input: {
            patientId: row.patientId,
            reason: row.reason,
            categories: row.categories,
            detail: row.detail,
          },
        },
      })
    },
    [createExemption],
  )

  const loading =
    (configsLoading && !configsData) || (exemptionsLoading && !exemptionsData)
  if (loading) return <Loading />

  return (
    <Box>
      <AdminPageHeader>
        <Typography variant="h6">Compliance</Typography>
      </AdminPageHeader>

      {/* Report */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <SectionHeader title="Report" />
        <Typography variant="body2">
          Download the latest compliance report as a CSV file. This report is
          generated daily and contains compliance status for all configured
          projects.
        </Typography>
        {reportError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {reportError}
          </Alert>
        )}
        <Box sx={{ mt: 3 }}>
          <Button
            type="primary"
            onClick={handleDownload}
            disabled={reportLoading}
            startIcon={<DownloadIcon />}
          >
            {reportLoading ? 'Fetching...' : 'Download Report'}
          </Button>
        </Box>
      </Paper>

      {/* Configurations */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <SectionHeader
          title={`Configurations (${filteredConfigs.length})`}
          action={
            <Button
              type="primary"
              size="small"
              onClick={() => {
                setEditingConfig(null)
                setConfigDialogOpen(true)
              }}
              startIcon={<AddIcon />}
            >
              New Configuration
            </Button>
          }
        />
        {configsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load configurations: {configsError.message}
          </Alert>
        )}
        <TextField
          placeholder="Search by project name, org, or ID..."
          value={configSearch}
          onChange={(e) => setConfigSearch(e.target.value)}
          size="small"
          fullWidth
          sx={{ mb: 2 }}
        />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Project</TableCell>
                <TableCell>Categories</TableCell>
                <TableCell>Threshold (days)</TableCell>
                <TableCell>Outreach Gap (days)</TableCell>
                <TableCell>Max Attempts</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredConfigs.map((config) => (
                <TableRow key={config.projectId} hover>
                  <TableCell>
                    {projectMap[config.projectId] && (
                      <Typography variant="body2" fontWeight="medium">
                        {projectMap[config.projectId].title}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      fontFamily="monospace"
                      color="text.secondary"
                    >
                      {config.projectId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {(config.categories || []).map((cat) => (
                        <Chip
                          key={cat}
                          label={formatCategory(cat)}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>{config.complianceThresholdDays}</TableCell>
                  <TableCell>{config.outreachGapDays}</TableCell>
                  <TableCell>
                    {config.maxOutreachAttempts != null
                      ? config.maxOutreachAttempts
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {config.updatedAt
                      ? formatDateString({
                          dateString: config.updatedAt,
                          format: 'MMM DD, YYYY',
                        })
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingConfig(config)
                        setConfigDialogOpen(true)
                      }}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteConfigId(config.projectId)}
                      title="Delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredConfigs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No configurations found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Exemptions */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <SectionHeader
          title={`Exemptions (${filteredExemptions.length})`}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                type="secondary"
                size="small"
                onClick={() => setCsvDialogOpen(true)}
                startIcon={<UploadFileIcon />}
              >
                Upload CSV
              </Button>
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  setEditingExemption(null)
                  setExemptionDialogOpen(true)
                }}
                startIcon={<AddIcon />}
              >
                New Exemption
              </Button>
            </Box>
          }
        />
        {exemptionsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load exemptions: {exemptionsError.message}
          </Alert>
        )}
        <TextField
          placeholder="Search by patient ID, reason, or detail..."
          value={exemptionSearch}
          onChange={(e) => setExemptionSearch(e.target.value)}
          size="small"
          fullWidth
          sx={{ mb: 2 }}
        />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Patient ID</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Categories</TableCell>
                <TableCell>Detail</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredExemptions.map((ex) => (
                <TableRow key={ex.patientId} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {ex.patientId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={formatReason(ex.reason)} size="small" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {(ex.categories || []).map((cat) => (
                        <Chip
                          key={cat}
                          label={formatCategory(cat)}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {ex.detail || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {ex.createdAt
                      ? formatDateString({
                          dateString: ex.createdAt,
                          format: 'MMM DD, YYYY',
                        })
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingExemption(ex)
                        setExemptionDialogOpen(true)
                      }}
                      title="Edit Exemption"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteExemptionId(ex.patientId)}
                      title="Delete Exemption"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredExemptions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No exemptions found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <ConfigurationDialog
        open={configDialogOpen}
        onClose={() => {
          setConfigDialogOpen(false)
          setEditingConfig(null)
        }}
        onSubmit={handleConfigSubmit}
        config={editingConfig}
        saving={creatingConfig || updatingConfig}
      />

      <Dialog open={!!deleteConfigId} onClose={() => setDeleteConfigId(null)}>
        <DialogTitle>Delete Configuration</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the compliance configuration for{' '}
            {projectMap[deleteConfigId]?.title ?? deleteConfigId}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button type="secondary" onClick={() => setDeleteConfigId(null)}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={() => handleDeleteConfig(deleteConfigId)}
            sx={{ backgroundColor: 'error.main' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ExemptionDialog
        open={exemptionDialogOpen}
        onClose={() => {
          setExemptionDialogOpen(false)
          setEditingExemption(null)
        }}
        onSubmit={handleExemptionSubmit}
        exemption={editingExemption}
        saving={creatingExemption || updatingExemption}
      />

      <CsvUploadDialog
        open={csvDialogOpen}
        onClose={() => setCsvDialogOpen(false)}
        onUpload={handleCsvUploadRow}
      />

      <Dialog
        open={!!deleteExemptionId}
        onClose={() => setDeleteExemptionId(null)}
      >
        <DialogTitle>Delete Exemption</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the compliance exemption for patient{' '}
            {deleteExemptionId}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button type="secondary" onClick={() => setDeleteExemptionId(null)}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={() => handleDeleteExemption(deleteExemptionId)}
            sx={{ backgroundColor: 'error.main' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CompliancePage
